import { Heatmap } from '@mui/x-charts-pro/Heatmap'
import { HeatmapValueType } from '@mui/x-charts-pro/models'
import {
  AnalyticsSource,
  FetchVenueAnalyticsGraphParams,
  VenueAnalyticsGraphBookingConversionHeatmap,
} from '@models/venue/VenueAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { Card, Empty, Flex, Typography } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { ConversionLegend, conversionColors as colors } from './ConversionLegend'

const { Title, Text } = Typography

// Grid axes. dayOfWeek follows ClickHouse toDayOfWeek semantics: 1=Mon..7=Sun.
const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 7] as const

// The party sizes the API always fills, even with no activity, so a venue with
// no bookings still renders a full grid.
const BASE_PARTY_SIZES = [1, 2, 3, 4, 5, 6, 7, 8] as const

/**
 * Party-size rows to draw: the guaranteed range plus every larger size the API
 * actually returned. One row per size, as reported — the endpoint aggregates
 * nothing, so folding 9 and above into an "8+" row would pool sizes that
 * convert differently and hide which one a venue struggles with.
 */
function resolvePartySizes(
  rows: VenueAnalyticsGraphBookingConversionHeatmap[]
): number[] {
  const sizes = new Set<number>(BASE_PARTY_SIZES)
  rows.forEach((row) => {
    if (row.partySize > 0) sizes.add(row.partySize)
  })
  return Array.from(sizes).sort((a, b) => a - b)
}

const getColorFromConversionRate = (conversionRate: number) => {
  if (conversionRate >= 60) {
    return colors.green
  } else if (conversionRate >= 30) {
    return colors.orange
  } else {
    return colors.red
  }
}

function getDayCode(dayOfWeek: number): string {
  return dayjs()
    .day(dayOfWeek === 7 ? 0 : dayOfWeek)
    .format('ddd')
}

function formatYAxisLabel(partySize: number): string {
  return `${partySize} guests`
}

interface HeatmapConfig {
  data: HeatmapValueType[]
  xAxis: string[]
  yAxis: string[]
  conversionRates: number[]
  searchCounts: number[]
  bookingCounts: number[]
}

interface CellCounts {
  search: number
  booking: number
}

function getCell(
  cellsByKey: Map<string, CellCounts>,
  partySize: number,
  dayOfWeek: number
): CellCounts {
  return cellsByKey.get(`${partySize}-${dayOfWeek}`) ?? { search: 0, booking: 0 }
}

/**
 * One entry per grid cell, keyed by the exact party size the API reported.
 *
 * Assumes at most one row per (partySize, dayOfWeek): the fetch below already
 * sums the per-venue responses into unique keys before they get here. Feeding
 * unsummed rows in would silently keep only the last venue's counts.
 */
function indexCells(
  rows: VenueAnalyticsGraphBookingConversionHeatmap[]
): Map<string, CellCounts> {
  const cellsByKey = new Map<string, CellCounts>()

  rows.forEach((row) => {
    if (row.partySize <= 0) return

    cellsByKey.set(`${row.partySize}-${row.dayOfWeek}`, {
      search: row.search,
      booking: row.booking,
    })
  })

  return cellsByKey
}

function transformToHeatmapData(
  rows: VenueAnalyticsGraphBookingConversionHeatmap[]
): HeatmapConfig {
  const partySizes = resolvePartySizes(rows)
  const cellsByKey = indexCells(rows)

  const conversionRates: number[] = []
  const searchCounts: number[] = []
  const bookingCounts: number[] = []
  const data: HeatmapValueType[] = []

  DAYS_OF_WEEK.forEach((dayOfWeek, dayIndex) => {
    partySizes.forEach((partySize, partySizeIndex) => {
      const cell = getCell(cellsByKey, partySize, dayOfWeek)
      const conversionRate =
        cell.search > 0 ? (cell.booking / cell.search) * 100 : 0

      conversionRates.push(conversionRate)
      searchCounts.push(cell.search)
      bookingCounts.push(cell.booking)

      data.push([dayIndex, partySizeIndex, cell.booking])
    })
  })

  const averageConversionByDay = DAYS_OF_WEEK.map((dayOfWeek) => {
    const cells = partySizes.map((partySize) =>
      getCell(cellsByKey, partySize, dayOfWeek)
    )
    const totalSearch = cells.reduce((sum, c) => sum + c.search, 0)
    const totalBooking = cells.reduce((sum, c) => sum + c.booking, 0)
    return totalSearch > 0 ? (totalBooking / totalSearch) * 100 : 0
  })

  const averageConversionByGuest = partySizes.map((partySize) => {
    const cells = DAYS_OF_WEEK.map((dayOfWeek) =>
      getCell(cellsByKey, partySize, dayOfWeek)
    )
    const totalSearch = cells.reduce((sum, c) => sum + c.search, 0)
    const totalBooking = cells.reduce((sum, c) => sum + c.booking, 0)
    return totalSearch > 0 ? (totalBooking / totalSearch) * 100 : 0
  })

  return {
    data,
    xAxis: DAYS_OF_WEEK.map(
      (dayOfWeek, i) =>
        `${getDayCode(dayOfWeek)} (${averageConversionByDay[i].toFixed(0)}%)`
    ),
    yAxis: partySizes.map(
      (partySize, i) =>
        `${formatYAxisLabel(partySize)} (${averageConversionByGuest[i].toFixed(0)}%)`
    ),
    conversionRates,
    searchCounts,
    bookingCounts,
  }
}

interface CustomCellProps {
  x: number
  y: number
  width: number
  height: number
  ownerState: {
    color: string
    value: number
    isHighlighted: boolean
    dataIndex: number
  }
}

function makeCustomCell(heatmapConfig: HeatmapConfig): React.FC<CustomCellProps> {
  const CustomCell: React.FC<CustomCellProps> = (props) => {
    const { x, y, width, height, ownerState, ...other } = props

    const conversionRate =
      heatmapConfig.conversionRates[ownerState.dataIndex] || 0
    const searchCount = heatmapConfig.searchCounts[ownerState.dataIndex] || 0
    const bookingCount = ownerState.value

    const customColor = getColorFromConversionRate(conversionRate)

    return (
      <>
        <rect
          {...other}
          x={x + 4}
          y={y + 4}
          width={width - 8}
          height={height - 8}
          fill={customColor}
          clipPath={
            ownerState.isHighlighted ? undefined : 'inset(0px round 6px)'
          }
        />

        <text
          x={x + width / 2}
          y={y + height / 2 - 14}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={11}
          fontWeight={600}
          fill="#1a1a1a"
          pointerEvents="none"
        >
          {searchCount} searchs
        </text>
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={11}
          fontWeight={600}
          fill="#1a1a1a"
          pointerEvents="none"
        >
          {bookingCount} bookings
        </text>
        <text
          x={x + width / 2}
          y={y + height / 2 + 14}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={11}
          fontWeight={600}
          fill="#1a1a1a"
          pointerEvents="none"
        >
          {conversionRate.toFixed(0)}%
        </text>
      </>
    )
  }

  return CustomCell
}

interface Props {
  venueIds: number[]
  dates?: [string, string]
  source?: AnalyticsSource
}

export default function ParametersHeatMap({ venueIds, dates, source }: Props) {
  const [rows, setRows] = React.useState<
    VenueAnalyticsGraphBookingConversionHeatmap[]
  >([])
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | undefined>(undefined)

  const venueIdsKey = venueIds.join(',')

  React.useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      if (venueIds.length === 0 || !dates) {
        setRows([])
        return
      }

      setIsLoading(true)
      setError(undefined)

      try {
        const results = await Promise.allSettled(
          venueIds.map((id) =>
            fetchAnalyticsGraph<
              VenueAnalyticsGraphBookingConversionHeatmap[]
            >({
              entity: 'venue',
              entityId: id,
              kind: 'booking-conversion-heatmap',
              startDate: dates[0],
              endDate: dates[1],
              source,
            })
          )
        )

        if (cancelled) return

        const aggregated = new Map<
          string,
          VenueAnalyticsGraphBookingConversionHeatmap
        >()

        for (const result of results) {
          if (result.status !== 'fulfilled') continue
          for (const row of result.value.data || []) {
            const key = `${row.partySize}-${row.dayOfWeek}`
            const existing = aggregated.get(key)
            if (existing) {
              existing.search += row.search
              existing.booking += row.booking
            } else {
              aggregated.set(key, { ...row })
            }
          }
        }

        setRows(Array.from(aggregated.values()))
      } catch (err) {
        if (cancelled) return
        console.error('Error fetching booking conversion heatmap:', err)
        setError('An error occurred while fetching data.')
        setRows([])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [venueIdsKey, dates, source])

  const heatmapConfig = React.useMemo(
    () => transformToHeatmapData(rows),
    [rows]
  )
  const CustomCell = React.useMemo(
    () => makeCustomCell(heatmapConfig),
    [heatmapConfig]
  )

  const hasData = rows.some((row) => row.search > 0)

  return (
    <Card loading={isLoading}>
      <Title level={5} className="m-0">
        Venue page: booking conversion by day and party size
      </Title>
      <Text type="secondary">
        Share of journeys that reached the slots screen on the venue page and
        went on to book. Bookings made straight from a tab, by tapping a slot in
        the venue list without opening the venue page, are excluded: they never
        see the slots screen, so they have no search to convert from.
      </Text>
      {!isLoading && (error || !hasData) && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={error || 'Not enough data available.'}
        />
      )}
      {!isLoading && !error && hasData && (
        <Flex vertical gap={16}>
          <Heatmap
            margin={{
              left: 0,
              right: 10,
              top: 20,
              bottom: 0,
            }}
            slots={{ cell: CustomCell, tooltip: () => null }}
            xAxis={[
              {
                data: heatmapConfig.xAxis,
                scaleType: 'band',
                disableTicks: true,
                disableLine: true,
              },
            ]}
            yAxis={[
              {
                data: heatmapConfig.yAxis,
                scaleType: 'band',
                disableTicks: true,
                width: 100,
                disableLine: true,
              },
            ]}
            series={[{ data: heatmapConfig.data }]}
            height={500}
          />
          <ConversionLegend mode="conversion" />
        </Flex>
      )}
    </Card>
  )
}
